import { useState, useEffect, useCallback, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { receiptApi } from 'src/services/receipts/api'
import { taskTypeEnum } from 'src/services/async-jobs/models'
import { useAsyncJobStore } from 'src/stores/async-jobs'
import { AiGeneratedExpense } from 'src/services/receipts'

type ReceiptJobState = {
  asyncJobId: string
}

interface UseReceiptAnalysisOptions {
  onResult: (data: AiGeneratedExpense, receiptId: string) => void
  onError?: (errorMessage: string, receiptId: string) => void
}

export function useReceiptAnalysis({ onResult, onError }: UseReceiptAnalysisOptions) {
  // Map of receiptId -> asyncJobId for active analyses
  const [activeJobs, setActiveJobs] = useState<Record<string, ReceiptJobState>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Track which jobs we've already processed to avoid double-firing
  const processedJobsRef = useRef<Set<string>>(new Set())

  // Subscribe to the async job store's recent array
  const recentJobs = useAsyncJobStore((s) => s.recent)
  const activeStoreJobs = useAsyncJobStore((s) => s.active)

  const { mutate: dispatchAnalysis, isPending: isDispatching } = useMutation({
    mutationFn: ({ receiptId, context }: { receiptId: string; context?: string }) =>
      receiptApi.csc.generateCustom({ receiptId, context }),
    onSuccess: (data, variables) => {
      setActiveJobs((prev) => ({
        ...prev,
        [variables.receiptId]: { asyncJobId: data.asyncJobId },
      }))
      // Clear any previous error for this receipt
      setErrors((prev) => {
        const next = { ...prev }
        delete next[variables.receiptId]
        return next
      })
    },
  })

  // Check for job completion/failure by watching the recent jobs in the store
  useEffect(() => {
    const entries = Object.entries(activeJobs)
    if (entries.length === 0) return

    for (const [receiptId, _jobState] of entries) {
      // Check if this job appeared in recent (completed or failed)
      const recentJob = recentJobs.find(
        (j) => j.taskType === taskTypeEnum.RECEIPT_ANALYSIS && j.entityId === receiptId,
      )
      if (!recentJob) continue

      // Skip if we already processed this job
      if (processedJobsRef.current.has(recentJob.id)) continue
      processedJobsRef.current.add(recentJob.id)

      if (recentJob.status === 'done' && recentJob.result) {
        onResult(recentJob.result as AiGeneratedExpense, receiptId)
      } else if (recentJob.status === 'failed') {
        const errorMsg = recentJob.errorMessage || 'Analysis failed'
        setErrors((prev) => ({ ...prev, [receiptId]: errorMsg }))
        onError?.(errorMsg, receiptId)
      }

      // Remove from active tracking
      setActiveJobs((prev) => {
        const next = { ...prev }
        delete next[receiptId]
        return next
      })
    }
  }, [recentJobs, activeJobs, onResult, onError])

  const isReceiptAnalyzing = useCallback(
    (receiptId: string): boolean => {
      // Check if we have an active dispatch or an active job in the store
      if (activeJobs[receiptId]) return true
      return activeStoreJobs.some(
        (j) => j.taskType === taskTypeEnum.RECEIPT_ANALYSIS && j.entityId === receiptId,
      )
    },
    [activeJobs, activeStoreJobs],
  )

  const getReceiptError = useCallback(
    (receiptId: string): string | undefined => errors[receiptId],
    [errors],
  )

  const clearReceiptError = useCallback((receiptId: string) => {
    setErrors((prev) => {
      const next = { ...prev }
      delete next[receiptId]
      return next
    })
  }, [])

  const analyzeReceipt = useCallback(
    (receiptId: string, context?: string) => {
      // Clear previous error
      setErrors((prev) => {
        const next = { ...prev }
        delete next[receiptId]
        return next
      })
      dispatchAnalysis({ receiptId, context })
    },
    [dispatchAnalysis],
  )

  const hasAnyActiveAnalysis = Object.keys(activeJobs).length > 0 || isDispatching

  return {
    analyzeReceipt,
    isReceiptAnalyzing,
    getReceiptError,
    clearReceiptError,
    hasAnyActiveAnalysis,
  }
}
